import { useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { CheckoutContext, CheckoutContextProps } from '../../../../context/CheckoutContext';
import { Company } from '../../../../hooks/useCompany';
import { buildVillageUrl } from '../../../../pages/Home/FitnessVillageHome/helpers/buildVillageUrl';
import useFitnessVillageCopy from '../../../../hooks/useFitnessVillageCopy';
import { selectFitnessVillages } from '../../../../Utils/fitnessVillageServices';
import { ASA_NAV } from '../../../../Utils/asaBrand';
import { usesAsaFlows } from '../../../../Utils/club15Brand';
import { shouldConsiderThisService } from '../../../Calendar/ServiceSelector/hooks/useServiceSelector';

export interface DfcVillageNavItem {
  name: string;
  /** An in-app path, or an absolute URL (opened in a new tab — see `isExternalNavTarget`). */
  to: string;
  active: boolean;
}

/** Absolute URLs leave the app; everything else is a router path. */
export const isExternalNavTarget = (to: string): boolean => /^https?:\/\//i.test(to);

/**
 * The Fitness Village theme's top-level nav — one source for the desktop header and the
 * mobile menu drawer, so the two can never list different pages.
 *
 * `company.sidebarContent` wins when the company has one (the same items and link shape the
 * shared Sidebar uses). Without it, DFC's three apply:
 *  - Home     → the village picker
 *  - Schedule → the first village's page, classes tab
 *  - Bookings → the first village's page, courts tab
 * Schedule and Bookings fall back to the home while the catalogue has no village yet.
 */
const useDfcVillageNavItems = (company?: Company): DfcVillageNavItem[] => {
  const { pathname, search } = useLocation();
  const { branchesMap, servicesMap } = useContext(CheckoutContext) as CheckoutContextProps;
  const currentPath = pathname.toLowerCase();
  const navCopy = useFitnessVillageCopy().header.nav;

  return useMemo(() => {
    const companyId = company?.id || '';
    // The village page opens on classes when the URL names no tab.
    const currentTab = new URLSearchParams(search).get('tab') === 'courts' ? 'courts' : 'classes';

    if (company?.sidebarContent?.length) {
      return company.sidebarContent.map(item => {
        const path = `/${item.path}`;
        const query = `companyId=${companyId}${item.params ? `&${item.params}` : ''}`;
        // Items that share a page (classes / courts on /FitnessVillage) are told apart by
        // their tab, so only the one for the tab on show is active.
        const itemTab = new URLSearchParams(item.params || '').get('tab');
        return {
          name: item.name,
          to: `${path}?${query}`,
          active: currentPath === path.toLowerCase() && (!itemTab || itemTab === currentTab),
        };
      });
    }

    const home = `/?companyId=${companyId}`;
    const firstVillageId = selectFitnessVillages(branchesMap, servicesMap)[0]?.id;
    const onVillagePage = currentPath === '/fitnessvillage';
    const tab = new URLSearchParams(search).get('tab');

    const items: DfcVillageNavItem[] = [
      { name: navCopy.home, to: home, active: currentPath === '/' },
      {
        name: navCopy.schedule,
        to: firstVillageId ? buildVillageUrl(companyId, firstVillageId, 'classes') : home,
        active: onVillagePage && tab !== 'courts' && tab !== 'gym',
      },
      {
        name: navCopy.bookings,
        to: firstVillageId ? buildVillageUrl(companyId, firstVillageId, 'courts') : home,
        active: onVillagePage && tab === 'courts',
      },
    ];

    // All Sports Arena and Club 15: Gym and Vouchers & Memberships, each only once the catalogue has one.
    if (usesAsaFlows(company)) {
      const gym = Array.from(servicesMap?.values() ?? []).find(
        service =>
          service.isActive !== false && shouldConsiderThisService(service, 'standard', 'gym')
      );
      if (gym) {
        items.push({
          name: ASA_NAV.gym,
          to: `/FitnessVillage?companyId=${companyId}&branchId=${gym.branchId}&tab=gym`,
          active: onVillagePage && tab === 'gym',
        });
      }
      if (company?.vouchers?.some(voucher => voucher.isActive ?? true)) {
        items.push({
          name: ASA_NAV.offers,
          to: `/Packages?companyId=${companyId}`,
          active: ['/packages', '/memberships', '/packagepurchase', '/membershippurchase'].includes(
            currentPath
          ),
        });
      }
    }

    return items;
  }, [company, currentPath, search, branchesMap, servicesMap, navCopy]);
};

export default useDfcVillageNavItems;
